/**
 * Device Token Helper Functions
 *
 * 用於管理與 tenant_id 綁定的 device_token，確保多租戶環境下的資料隔離。
 * 使用格式：device_token_${tenantId}
 */

/**
 * 取得指定租戶的 device token
 * @param tenantId - 租戶 ID，如果為 null 則嘗試從 localStorage 讀取當前 tenant_id
 * @returns device token 或 null
 */
export function getDeviceToken(tenantId: string | null): string | null {
  // 如果沒有提供 tenantId，嘗試從 localStorage 讀取當前 tenant_id
  const targetTenantId = tenantId || localStorage.getItem('tenant_id');

  if (!targetTenantId) {
    return null;
  }

  const key = `device_token_${targetTenantId}`;
  return localStorage.getItem(key);
}

/**
 * 設置指定租戶的 device token
 * @param tenantId - 租戶 ID
 * @param token - device token
 */
export function setDeviceToken(tenantId: string, token: string): void {
  if (!tenantId || !token) {
    console.warn('[deviceToken] setDeviceToken: tenantId 或 token 為空');
    return;
  }

  const key = `device_token_${tenantId}`;
  localStorage.setItem(key, token);
}

/**
 * 移除指定租戶的 device token
 * @param tenantId - 租戶 ID
 */
export function removeDeviceToken(tenantId: string): void {
  if (!tenantId) {
    return;
  }

  const key = `device_token_${tenantId}`;
  localStorage.removeItem(key);
}

/**
 * 清除所有租戶的 device token
 * 用於離開商店時，確保不會留下任何 device token
 */
export function clearAllDeviceTokens(): void {
  const keysToRemove: string[] = [];

  // 收集所有 device_token_ 開頭的 key
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('device_token_')) {
      keysToRemove.push(key);
    }
  }

  // 移除所有收集到的 key
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  // 也清除舊格式的 device_token（向後相容）
  localStorage.removeItem('device_token');
}

/**
 * 向後相容：檢查是否存在舊格式的 device_token
 * 如果存在且當前有 tenant_id，則遷移到新格式
 */
export function migrateLegacyDeviceToken(): void {
  const legacyToken = localStorage.getItem('device_token');
  const currentTenantId = localStorage.getItem('tenant_id');

  if (legacyToken && currentTenantId) {
    // 遷移到新格式
    setDeviceToken(currentTenantId, legacyToken);
    // 清除舊格式
    localStorage.removeItem('device_token');
    console.log('[deviceToken] 已將舊格式 device_token 遷移到新格式');
  }
}

/**
 * 自動註冊裝置（Member 登入時使用）
 * @param tenantId - 租戶 ID
 * @param tenantToken - 租戶 Token
 * @returns Promise<boolean> - 是否註冊成功
 */
export async function autoRegisterDevice(
  tenantId: string,
  tenantToken: string
): Promise<boolean> {
  try {
    const apiBase =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

    // 生成唯一的裝置 ID（使用瀏覽器指紋 + 時間戳）
    const deviceId = `pos-device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const deviceName = navigator.userAgent.includes('Mobile')
      ? 'POS 平板/手機'
      : 'POS 裝置';

    const response = await fetch(`${apiBase}/api/devices/register`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tenantToken}`,
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        device_id: deviceId,
        device_name: deviceName,
      }),
    });

    const data = await response.json();

    if (data.success && data.data?.token) {
      // 保存 Device Token
      setDeviceToken(tenantId, data.data.token);
      console.log('[deviceToken] 自動註冊裝置成功');
      return true;
    } else {
      console.error('[deviceToken] 自動註冊裝置失敗:', data.error);
      return false;
    }
  } catch (error) {
    console.error('[deviceToken] 自動註冊裝置時發生錯誤:', error);
    return false;
  }
}

/**
 * 刪除當前裝置（離開商店時使用）
 * @param tenantId - 租戶 ID
 * @param tenantToken - 租戶 Token
 * @returns Promise<boolean> - 是否刪除成功
 */
export async function deleteCurrentDevice(
  tenantId: string,
  tenantToken: string
): Promise<boolean> {
  try {
    const deviceToken = getDeviceToken(tenantId);
    if (!deviceToken) {
      // 沒有 device token，視為已刪除
      return true;
    }

    const apiBase =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

    const response = await fetch(`${apiBase}/api/devices/current`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${tenantToken}`,
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        device_token: deviceToken,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // 清除本地儲存的 device token
      removeDeviceToken(tenantId);
      console.log('[deviceToken] 刪除裝置成功');
      return true;
    } else {
      console.error('[deviceToken] 刪除裝置失敗:', data.error);
      // 即使 API 失敗，也清除本地儲存
      removeDeviceToken(tenantId);
      return false;
    }
  } catch (error) {
    console.error('[deviceToken] 刪除裝置時發生錯誤:', error);
    // 即使發生錯誤，也清除本地儲存
    removeDeviceToken(tenantId);
    return false;
  }
}
